/**
 * FoundersNote.js — Peer-to-peer value proposition letter.
 * For owners of experiential stays across the Himalayas.
 * Tone: from one host to another. Minimal words. No sales.
 * A4 single page. Downloadable as PDF.
 */
import React, { useRef } from 'react';
import { Printer, MessageCircle, Mountain } from 'lucide-react';

const pf = { fontFamily: "'Playfair Display', Georgia, serif" };
const GH_BLUE = '#2C5F8D';
const OHSM_LOGO = 'https://static.wixstatic.com/media/d5f536_1a88aa9139e8401bbd6cbd68b790254c~mv2.png/v1/fill/w_82,h_82,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/Logo%2001.png';
const CTA_NUMBER = '9871155963';
const CTA_INTL = '919871155963';
const APPLY_URL = 'https://gohimalayan.com/apply';
const OHSM_URL = 'https://www.ohsm.in/';
const WA_LINK = `https://wa.me/${CTA_INTL}?text=${encodeURIComponent("Hi — saw the GoHimalayan × OHSM note. Would like to talk.")}`;

export default function FoundersNote() {
  const docRef = useRef(null);

  const download = () => {
    // Use the browser's native print pipeline. Chromium renders the DOM
    // directly (unlike html2canvas which struggles with Tailwind classes
    // inside a cloned iframe). Users pick "Save as PDF" in the print dialog.
    // Works on desktop Chrome/Safari/Edge and on mobile Chrome/Safari.
    window.print();
  };

  return (
    <div className="min-h-screen bg-stone-100 py-8 px-4">
      {/* Screen controls */}
      <div className="max-w-4xl mx-auto mb-6 flex flex-col sm:flex-row gap-3 items-center justify-between print:hidden">
        <p className="text-sm text-stone-500">A note between hosts · shareable</p>
        <div className="flex gap-3">
          <a href={WA_LINK} target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-lg text-sm font-medium"
             data-testid="founders-whatsapp">
            <MessageCircle className="h-4 w-4" /> WhatsApp us
          </a>
          <button onClick={download}
                  className="inline-flex items-center gap-2 bg-stone-900 hover:bg-stone-800 text-white px-5 py-2.5 rounded-lg text-sm font-medium"
                  data-testid="founders-download-pdf">
            <Printer className="h-4 w-4" /> Save as PDF
          </button>
        </div>
      </div>

      {/* A4 document */}
      <div ref={docRef}
           id="founders-doc"
           className="mx-auto bg-white shadow-xl relative"
           style={{ width: '210mm', height: '297mm', padding: '14mm 16mm', boxSizing: 'border-box', overflow: 'hidden' }}>

        {/* subtle background flourish */}
        <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full opacity-[0.04]"
             style={{ backgroundColor: GH_BLUE }} />

        {/* ─── Joint branding header ─── */}
        <header className="pb-5 border-b border-stone-300 relative">
          <p className="text-[9px] tracking-[0.35em] uppercase text-stone-400 mb-3">Joining hands · Announcing</p>
          <div className="flex items-center justify-between gap-6">
            <div className="flex items-center gap-2.5">
              <Mountain className="h-9 w-9" strokeWidth={2.5} style={{ color: GH_BLUE }} />
              <div>
                <p className="text-2xl font-bold tracking-tight leading-none" style={{ ...pf, color: GH_BLUE }}>GoHimalayan</p>
                <p className="text-[9px] tracking-[0.28em] uppercase text-stone-500 mt-2">Discover Unique Himalayan Stays</p>
              </div>
            </div>
            <span className="text-3xl font-light" style={{ color: GH_BLUE, opacity: 0.5, ...pf }}>×</span>
            <div className="flex items-center gap-2.5">
              <img src={OHSM_LOGO} alt="OHSM" crossOrigin="anonymous"
                   style={{ width: '42px', height: '42px' }} />
              <div>
                <p className="text-2xl font-bold tracking-tight leading-none text-stone-900" style={pf}>OHSM</p>
                <p className="text-[9px] tracking-[0.12em] uppercase text-stone-500 mt-2">Simplifying Hospitality Operations</p>
              </div>
            </div>
          </div>
        </header>

        {/* ─── Salutation ─── */}
        <section className="pt-6">
          <p className="text-[11px] tracking-[0.35em] uppercase mb-3" style={{ color: GH_BLUE }}>A note from one host to another</p>
          <h1 className="text-[30px] leading-[1.1] text-stone-900 mb-4" style={pf}>
            You built something rare.<br/>
            <span className="italic">We built the two things it deserves.</span>
          </h1>
        </section>

        {/* ─── The Letter body ─── */}
        <section className="pt-3">
          <div className="text-[12.5px] text-stone-800 leading-[1.7] space-y-3.5" style={{ maxWidth: '175mm' }}>
            <p>
              We run <a href="https://gohimalayan.com/collective/bluehouse" target="_blank" rel="noreferrer" className="font-semibold underline decoration-1 underline-offset-2 hover:opacity-80" style={{ color: GH_BLUE }} data-testid="founders-bluehouse-link">Bluehouse by the River</a> in Tirthan. Six rooms, a
              trout stream, the same everyday problems every experiential owner
              in the mountains knows too well.
            </p>
            <p>
              Guests find us on OTAs and we pay 15–25 % away in commission on
              every booking. The property runs on paper registers and one
              overworked WhatsApp. Instagram sits neglected for weeks. The photos
              we send an OTA get squeezed into a template that has nothing to do
              with the place we actually built.
            </p>
            <p>
              We got tired of it. So we built <strong style={{ color: GH_BLUE }}>GoHimalayan</strong> —
              a curated home for experiential stays where guests come, read your
              story, and book on <em>your</em> WhatsApp. Zero commission. Ever.
            </p>
            <p>
              And now we’ve joined hands with <strong>OHSM</strong> — a
              Delhi-NCR based property management app built for stays exactly
              like ours. The kind of app you can actually use, on a phone,
              between check-ins.
            </p>
            <p>
              One bundle. One flat annual price. Three things that matter.
            </p>
          </div>
        </section>

        {/* ─── 3 pillars ─── */}
        <section className="pt-6">
          <div className="grid grid-cols-3 gap-4">
            {[
              { n: 'One', t: 'Your page on GoHimalayan', b: 'Direct WhatsApp booking. Zero commission. Every enquiry lands on your phone.' },
              { n: 'Two', t: 'OHSM on your phone', b: 'The whole back-office — bookings, billing, staff, expenses — in one app.' },
              { n: 'Three', t: 'Featured on our IG & FB', b: 'Your property gets shown to our audience of high-intent Himalayan travellers.' },
            ].map((p) => (
              <div key={p.n} className="border-l-2 pl-3.5" style={{ borderColor: GH_BLUE }}>
                <p className="text-[10px] tracking-[0.3em] uppercase mb-1" style={{ color: GH_BLUE }}>{p.n}</p>
                <p className="text-[13.5px] font-bold text-stone-900 leading-tight mb-1.5" style={pf}>{p.t}</p>
                <p className="text-[10.5px] text-stone-600 leading-snug">{p.b}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ─── Pricing strip — kept small, peer register ─── */}
        <section className="pt-5">
          <div className="flex items-center gap-3 text-[12px] text-stone-700 border-l-2 pl-3.5 flex-wrap" style={{ borderColor: GH_BLUE }}>
            <span className="text-[10px] tracking-[0.3em] uppercase" style={{ color: GH_BLUE }}>One flat price</span>
            <span className="text-stone-300">·</span>
            <span className="font-semibold text-stone-900">₹11,999 / year</span>
            <span className="text-stone-500">upto 10 rooms</span>
            <span className="text-stone-400 italic text-[11px] ml-auto">A single season of direct bookings pays it back.</span>
          </div>
        </section>

        {/* ─── Sign-off ─── */}
        <section className="pt-5">
          <p className="text-[12.5px] text-stone-800 leading-relaxed mb-4" style={{ maxWidth: '175mm' }}>
            We’re opening this to a small, hand-picked group of experiential owners
            we already admire. If that&rsquo;s you — write to us. It’s a five-minute
            conversation. And if it fits, we&rsquo;ll set the whole thing up for you inside
            of a week.
          </p>
          <div className="flex items-end justify-between gap-6 mt-4">
            <div>
              <p className="text-[20px] leading-none mb-1" style={pf}>Warmly,</p>
              <p className="text-[13px] text-stone-800 font-semibold mt-3">The founders,</p>
              <p className="text-[12px] text-stone-600">GoHimalayan &nbsp;×&nbsp; OHSM</p>
            </div>
            <div className="text-right text-[11px] text-stone-500">
              <p>Tirthan Valley · Delhi-NCR</p>
              <p>2026</p>
            </div>
          </div>
        </section>

        {/* ─── CTA — 3 links ─── */}
        <section className="pt-5 mt-4 border-t border-stone-300">
          <div className="grid grid-cols-3 gap-3">
            <a href={APPLY_URL} target="_blank" rel="noreferrer"
               className="bg-stone-50 border rounded-lg p-2.5 hover:shadow-md transition-shadow"
               style={{ borderColor: GH_BLUE }}
               data-testid="founders-cta-apply">
              <p className="text-[9px] tracking-[0.25em] uppercase mb-1" style={{ color: GH_BLUE }}>1 · List your property</p>
              <p className="text-[11px] font-semibold text-stone-900 leading-tight">gohimalayan.com/apply</p>
              <p className="text-[10px] text-stone-500 mt-1 leading-snug">Fill the form. We curate your page within a week.</p>
            </a>
            <a href={OHSM_URL} target="_blank" rel="noreferrer"
               className="bg-stone-50 border rounded-lg p-2.5 hover:shadow-md transition-shadow"
               style={{ borderColor: GH_BLUE }}
               data-testid="founders-cta-ohsm">
              <p className="text-[9px] tracking-[0.25em] uppercase mb-1" style={{ color: GH_BLUE }}>2 · Get the app</p>
              <p className="text-[11px] font-semibold text-stone-900 leading-tight">ohsm.in</p>
              <p className="text-[10px] text-stone-500 mt-1 leading-snug">Download OHSM. We&rsquo;ll help you set it up.</p>
            </a>
            <a href={WA_LINK} target="_blank" rel="noreferrer"
               className="rounded-lg p-2.5" style={{ backgroundColor: GH_BLUE, color: 'white' }}
               data-testid="founders-cta-whatsapp">
              <p className="text-[9px] tracking-[0.25em] uppercase text-amber-200 mb-1">3 · Or just say hi</p>
              <p className="text-[12px] font-bold whitespace-nowrap leading-tight">+91 98711 55963</p>
              <p className="text-[12px] font-bold whitespace-nowrap leading-tight">+91 88671 38542</p>
              <p className="text-[10px] text-stone-100 mt-1 leading-snug">WhatsApp us any time.</p>
            </a>
          </div>
        </section>

        {/* Footer */}
        <footer className="pt-3 text-[9px] text-stone-400 flex justify-between">
          <span>GoHimalayan Editorial Commons · gohimalayan.com</span>
          <span>OHSM · Startup India recognised</span>
        </footer>
      </div>

      <style>{`
        @media print {
          /* Reset the page to just the A4 document */
          html, body { background: white !important; margin: 0 !important; padding: 0 !important; }
          .print\\:hidden { display: none !important; }
          /* Hide everything except our document and its ancestors */
          body * { visibility: hidden; }
          #founders-doc, #founders-doc * { visibility: visible; }
          #founders-doc { position: absolute; left: 0; top: 0; margin: 0 !important; box-shadow: none !important; }
        }
        @page { size: A4; margin: 0; }
      `}</style>
    </div>
  );
}
